<template>
  <div
    :id="zoneId"
    class="tender-award-attach-po"
  >
    <dropzone
      v-if="!readonly && dropzoneHeaders"
      :id="`dropzone-${new Date().getTime()}`"
      ref="dropzone"
      :url="`${baseURL}/docs/`"
      :maxFileSizeInMB="docUploadSize"
      :maxNumberOfFiles="100"
      :acceptedFileTypes="docUploadTypes"
      :headers="dropzoneHeaders"
      uploadMulti="true"
      :previewTemplate="template"
      :hiddenInputContainer="hiddenInputContainer"
      @vdropzone-file-added="clearMessages"
      @vdropzone-sending="preSendRequest"
      @vdropzone-success="uploadSuccess"
      @vdropzone-error="setErrorMessage"
      @vdropzone-queue-complete="queueComplete"
    >
      <div class="dz-default dz-message  attach-po-btn inputContainer">
        <span class="btn btn--darkblue">{{ btnName }}</span>
      </div>
      <input
        type="hidden"
        name="token"
        value="file upload"
      >
    </dropzone>
    <div
      v-show="totalCount"
      style="color: #0C3A58"
      class="clearfix"
    >
      <span>{{ totalCount }} files uploading</span>
    </div>
    <div
      v-show="uploadResult"
      style="color: #0C3A58"
      class="clearfix"
    >
      <span>{{ uploadResult }}</span>
    </div>
    <div v-show="errorMessage">
      <span>{{ errorMessage }}</span>
    </div>

    <div class="clearfix" /><!-- layout helper, prevent table margin top collapse -->
    <table
      v-show="files.length>0"
      class="tender-tnc-table"
    >
      <thead>
        <th class="number tender-font">
          #
        </th>
        <th class="filename tender-font">
          {{ $t('tender.attachment.document') }}
        </th>
        <th v-if="!(readonly || isAllFreeze)" />
      </thead>
      <tbody>
        <template v-for="(item, index) in files">
          <tr
            :key="item.id"
            class="set-border-top"
          >
            <td class="number tender-font">
              {{ index + 1 }}
            </td>
            <td class="filename tender-font">
              <a
                style="text-decoration: underline; word-break: break-all;"
                @click="downloadFile(item.id)"
              >
                {{ item.filename }}
              </a>
            </td>
            <td
              v-if="!(readonly || isAllFreeze)"
              class="description tender-font"
            >
              <div
                v-if="!isFreeze(item)"
                class="file-upload-delete"
                @click="deleteFile(index, item)"
              >
                &#x2715;
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script>
import Dropzone from 'vue2-dropzone'
import { mapGetters } from 'vuex'
import lodash from 'lodash'
import arrayHelper from '@/utils/arrayHelper'
import * as xfs from '@/utils/xfs'
import { SETTING_CODE } from '@/api/settings/consts'
import { useBaseUrl } from '@/utils/http/hook'
import { getAttachments, deleteDocument } from '@/api/docs/request'
import httpHeaderMixin from '@/controls/httpHeaderMixin'
import genRandomID from '@/utils/genRandomID'

export default {
  name: 'AttachDocs',
  components: {
    Dropzone,
  },
  mixins: [httpHeaderMixin],
  props: {
    resourceId: {
      type: String,
      default: undefined,
    },
    btnName: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    freezeDocTime: {
      type: String,
      default: '',
    },
  },

  data () {
    const zoneId = `hkld-dropzone-${genRandomID()}`
    return {
      errorMessage: '',
      successCount: 0,
      errorCount: 0,
      totalCount: 0,
      uploadResult: '',
      files: [],
      baseURL: useBaseUrl(),
      dropzoneHeaders: null,
      zoneId,
      hiddenInputContainer: `#${zoneId} .inputContainer`,
    }
  },

  computed: {
    ...mapGetters('settings', {
      getSettingByCode: 'setting',
    }),
    ...mapGetters('regions', ['currentRegion']),
    docUploadSize () {
      return parseInt(this.getSettingByCode(SETTING_CODE.docUploadSize), 10) || 10
    },
    docUploadTypes () {
      return this.getSettingByCode(SETTING_CODE.docUploadTypes) || 'application/pdf,image/png,image/jpeg,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    isAllFreeze () {
      return this.freezeDocTime && this.files.every(item => this.isFreeze(item))
    },
  },
  watch: {
    files (newValue) {
      this.$emit('update:attachments', newValue)
    },
    async resourceId () {
      if (!this.resourceId) {
        return
      }
      await this.loadFiles()
      this.successCount = 0
      this.errorCount = 0
      this.totalCount = 0
      this.uploadResult = ''
      this.errorMessage = ''
    },
  },
  async mounted () {
    this.dropzoneHeaders = await this.getHttpHeaders()
    await this.loadFiles()
  },
  methods: {

    preSendRequest (files, xhr, formData) {
      formData.append('src', this.resourceId)
      this.uploadResult = ''
    },

    queueComplete () {
      this.uploadResult = this.$t('hint.fileUploadSuccess', [this.successCount, this.totalCount])
      if ((this.successCount + this.errorCount) === this.totalCount) {
        this.successCount = 0
        this.errorCount = 0
        this.totalCount = 0
      }
    },

    template () {
      return `<div>
                <div class="dz-error-message"><span data-dz-errormessage></span></div>
              </div>`
    },

    async deleteFile (index, item) {
      const deleted = await deleteDocument(item.id)
      if (deleted) {
        this.resetMessages()
        this.files.splice(index, 1)
        if (this.$refs.dropzone) {
          this.$refs.dropzone.removeFile(item)
        }
      }
    },

    async downloadFile (fileId) {
      await xfs.downloadFile(`/docs/${fileId}`)
    },

    setErrorMessage (file) {
      if (file.status !== 'canceled') {
        if (!file.accepted && file.upload.progress === 0) {
          const oversizeMessage = $(file.previewElement).find('.dz-error-message').text().replace(/MiB/g, 'MB')
          this.appendErrorMessage(`File ${file.name} upload error: ${oversizeMessage}`)
        } else {
          this.appendErrorMessage(`File ${file.name} upload error!`)
        }
      }
    },

    appendErrorMessage (msg) {
      this.errorCount++
      const newline = this.errorMessage ? '\n' : ''
      this.errorMessage = this.errorMessage.concat(newline).concat(msg)
    },

    clearMessages () {
      if (!this.totalCount) {
        this.errorMessage = ''
      }
      this.totalCount++
    },

    resetMessages () {
      this.uploadResult = ''
      this.errorMessage = ''
    },

    uploadSuccess (file) {
      if (file.xhr) {
        const xhrResponse = file.xhr.response
        const xhrStatus = file.xhr.status
        if (xhrStatus >= 200 && xhrStatus <= 300 && xhrResponse) {
          const newItem = JSON.parse(xhrResponse).data[0]
          if (!newItem.isNewRecord) {
            const duplicateNameMessage = `File ${newItem.filename} upload error: duplicate file name`
            this.appendErrorMessage(duplicateNameMessage)
            return false
          }
          arrayHelper.addNewItem(this.files, newItem)
        }
      }
      this.successCount++
    },

    async loadFiles () {
      if (!this.resourceId) return
      this.files = await getAttachments(this.resourceId)
    },

    isFreeze (file) {
      const isFreezeByAutoCreation = lodash.get(file, 'metaData.fromAutoCreationJob') || false
      if (isFreezeByAutoCreation) return true
      if (!this.freezeDocTime) return false
      const fileCratedDate = new Date(file.createdAt)
      const freezeDocDate = new Date(this.freezeDocTime)
      const isFreezeByTime = fileCratedDate < freezeDocDate
      return isFreezeByTime
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/attachments';

.tender-award-attach-po {
  display: inline-block;
  height: auto;
  width: 100%;
  margin-top: 10px;

  .dropzone {
    float: right;
    height: 44px;
    margin-left: 110px;
    min-height: 44px;
    border: none;
    background: transparent;
    padding: 0;

    .dz-message {
      margin: 0;
      padding: 10px 0;
    }
  }
}
.vue-dropzone.dropzone {
  margin-bottom: 0;
}
.attach-po-btn {
  padding-top: 10px;
}

.tender-tnc-table {
  margin-top: 10px;
  padding-top: 10px;
}
.filename {
  width: 80%;
}
</style>
<style lang="scss">
.tender-award-attach-po {
  .dropzone.dz-started .dz-message {
    display: block;
  }
  .dz-complete {
    display: none;
  }
}
</style>
